import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Chrome,
  LogOut,
  Mic,
  Phone,
  Send,
  FileText,
  MessageSquare,
  User as UserIcon,
  Loader2,
  X,
  CheckCircle,
  AlertCircle,
  Mail,
  Lock,
  ArrowLeft,
  Eye,
  EyeOff,
  FolderOpen,
} from "lucide-react";
import { toast } from "sonner";
import { FloatingOrbs, AmbientDust, PulseDot } from "@/components/landing/AmbientMotion";
import MediaUploader from "@/components/media/MediaUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * /client-portal — gated Google sign-in landing.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 * On URL fragment `#session_id=...`, we exchange it with the backend, which
 * validates against paid_clients + guest_requests. If not on file, we surface
 * the lead-capture modal so we can follow up.
 */

const StatusBadge = ({ status }) => {
  const map = {
    confirmed: { bg: "#22c55e22", border: "#22c55e55", text: "#22c55e" },
    booked_confirmed: { bg: "#22c55e22", border: "#22c55e55", text: "#22c55e" },
    pending_review: { bg: "#a78bfa22", border: "#a78bfa55", text: "#a78bfa" },
    pending: { bg: "#a78bfa22", border: "#a78bfa55", text: "#a78bfa" },
    completed: { bg: "#00f0ff22", border: "#00f0ff55", text: "#00f0ff" },
    rejected: { bg: "#ff005522", border: "#ff005555", text: "#ff5577" },
    cancelled: { bg: "#ffffff11", border: "#ffffff33", text: "#a395c3" },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold border"
      style={{ background: s.bg, borderColor: s.border, color: s.text }}
    >
      {status || "unknown"}
    </span>
  );
};

const DenialModal = ({ open, email, name, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, name: name || "", email: email || "" }));
      setDone(false);
    }
  }, [open, email, name]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/prospects`, {
        ...form,
        source: "client-portal-denial",
      });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try emailing info@sbmediastudio.com.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        data-testid="portal-denial-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0d0716]/95 p-7 md:p-8"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition"
            data-testid="portal-denial-close"
          >
            <X size={16} />
          </button>

          {!done ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-[#ffb800]/15 border border-[#ffb800]/40 text-[#ffb800] flex items-center justify-center mb-4">
                <AlertCircle size={20} />
              </div>
              <h2 className="font-display font-black text-2xl tracking-tight leading-tight">
                Almost there — let&apos;s get you approved.
              </h2>
              <p className="text-[#a395c3] text-sm mt-2">
                The Client Portal is reserved for active SB Media clients & Spotlight guests.
                Drop your info below — the studio gets notified instantly, and you&apos;ll receive
                an email the moment you&apos;re approved.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="portal-denial-form">
                <input
                  required
                  placeholder="Full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="portal-denial-name"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="portal-denial-email"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
                <input
                  placeholder="Phone (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="portal-denial-phone"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
                <input
                  placeholder="Business name (optional)"
                  value={form.business_name}
                  onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                  data-testid="portal-denial-business"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
                <textarea
                  placeholder="Anything we should know? (optional)"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="portal-denial-message"
                  className="w-full rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm resize-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="portal-denial-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold py-3 hover:bg-white transition disabled:opacity-60"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Send my info
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="portal-denial-success">
              <div className="w-14 h-14 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={24} />
              </div>
              <h2 className="font-display font-black text-2xl tracking-tight">
                You&apos;re on the list.
              </h2>
              <p className="text-[#a395c3] text-sm mt-3">
                The studio was pinged the moment you hit submit — we&apos;ll email you
                at <span className="text-white">{form.email}</span> as soon as your Portal
                access is approved. Usually within a day.
              </p>
              <a
                href="/"
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-white/10 text-white text-sm hover:bg-white/20 transition"
              >
                Back to homepage
              </a>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/**
 * Sign-in surface — offers Google + email/password side by side.
 * Tabs: [Sign in] · [Create account] · [Forgot password]
 */
const PortalAuthSurface = ({ onGoogleClick, onSignedIn }) => {
  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const formatDetail = (detail) => {
    if (detail == null) return "Something went wrong. Try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "login") {
        await axios.post(`${API}/client/auth/login`, { email, password });
        onSignedIn && onSignedIn();
      } else if (mode === "register") {
        await axios.post(`${API}/client/auth/register`, {
          email, password, name, phone, business_name: businessName,
        });
        setNotice("You're on the list — the studio was pinged and you'll get an email once you're approved.");
        setMode("login");
        setPassword("");
      } else if (mode === "forgot") {
        await axios.post(`${API}/client/auth/forgot`, { email });
        setNotice("If that email exists, a reset link is on its way. Check your inbox.");
      }
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-md mx-auto pt-8"
      data-testid="portal-signin"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#22c55e] mb-4">
          <PulseDot color="#22c55e" size={7} />
          Client Portal
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
          {mode === "register" ? (
            <>Create your <span className="text-[#22c55e] text-glow-cyan">account.</span></>
          ) : mode === "forgot" ? (
            <>Reset your <span className="text-[#a78bfa] text-glow-cyan">password.</span></>
          ) : (
            <>Sign in to the <span className="text-[#00f0ff] text-glow-cyan">portal.</span></>
          )}
        </h1>
        <p className="text-[#a395c3] text-sm mt-4">
          {mode === "register"
            ? "Anyone can sign up — new accounts go through a quick admin approval before portal access opens up."
            : "For existing clients and Spotlight guests. Your bookings, invoices, and chat history — all in one place."}
        </p>
      </div>

      {/* Google */}
      {mode !== "forgot" && (
        <>
          <button
            onClick={onGoogleClick}
            data-testid="portal-google-signin"
            className="mt-8 w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 rounded-full bg-white text-[#05050a] font-semibold text-sm hover:bg-[#00f0ff] transition-colors"
          >
            <Chrome size={16} /> Continue with Google
          </button>
          <div className="mt-6 flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-white/30">
            <div className="flex-1 h-px bg-white/10" /> or with email <div className="flex-1 h-px bg-white/10" />
          </div>
        </>
      )}

      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="portal-email-form">
        {mode === "register" && (
          <>
            <div>
              <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-2 mb-1.5">
                <UserIcon size={11} /> Your name
              </label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                required data-testid="portal-register-name"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-2 mb-1.5">
                  <Phone size={11} /> Phone
                </label>
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  data-testid="portal-register-phone"
                  className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-1.5 block">Business</label>
                <input
                  type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  data-testid="portal-register-business"
                  className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                />
              </div>
            </div>
          </>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-2 mb-1.5">
            <Mail size={11} /> Email
          </label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required data-testid="portal-auth-email"
            className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#00f0ff]/60"
            autoComplete="email"
          />
        </div>
        {mode !== "forgot" && (
          <div>
            <label className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-2 mb-1.5">
              <Lock size={11} /> Password {mode === "register" && <span className="normal-case tracking-normal text-white/40">· min 8 chars</span>}
            </label>
            <div className="relative">
              <input
                type={showPw ? "text" : "password"}
                value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={mode === "register" ? 8 : undefined}
                data-testid="portal-auth-password"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 pr-12 text-sm focus:outline-none focus:border-[#00f0ff]/60"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
              />
              <button
                type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md hover:bg-white/[0.06] flex items-center justify-center"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div role="alert" data-testid="portal-auth-error" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#ff0055]/10 border border-[#ff0055]/40 text-[#ff0055] text-xs">
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}
        {notice && (
          <div role="status" data-testid="portal-auth-notice" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#22c55e]/10 border border-[#22c55e]/40 text-[#22c55e] text-xs">
            <CheckCircle size={13} className="flex-shrink-0 mt-0.5" /> <span>{notice}</span>
          </div>
        )}

        <button
          type="submit" disabled={busy}
          data-testid="portal-auth-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 rounded-full bg-[#00f0ff] text-black font-bold text-sm hover:bg-white transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : null}
          {mode === "login" && "Sign in"}
          {mode === "register" && "Create account"}
          {mode === "forgot" && "Send reset link"}
        </button>
      </form>

      <div className="mt-6 text-center text-[11px] text-[#a395c3] space-y-2">
        {mode === "login" && (
          <>
            <div>
              New here?{" "}
              <button onClick={() => { setMode("register"); setError(""); setNotice(""); }} className="text-[#22c55e] hover:text-white" data-testid="portal-switch-register">Create an account</button>
            </div>
            <div>
              Forgot your password?{" "}
              <button onClick={() => { setMode("forgot"); setError(""); setNotice(""); }} className="text-[#a78bfa] hover:text-white" data-testid="portal-switch-forgot">Reset it</button>
            </div>
          </>
        )}
        {mode !== "login" && (
          <button onClick={() => { setMode("login"); setError(""); setNotice(""); }} className="text-[#00f0ff] hover:text-white inline-flex items-center gap-1" data-testid="portal-switch-login">
            <ArrowLeft size={11} /> Back to sign in
          </button>
        )}
      </div>
    </motion.div>
  );
};


const ClientPortal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const processedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [portal, setPortal] = useState(null);
  const [denial, setDenial] = useState(null); // { email, name } when denied

  const [phoneEdit, setPhoneEdit] = useState("");
  const [businessEdit, setBusinessEdit] = useState("");
  const [savingContact, setSavingContact] = useState(false);

  // Sync from Google → backend
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("session_id=") && !processedRef.current) {
      processedRef.current = true;
      const session_id = hash.split("session_id=")[1].split("&")[0];
      // Clean the URL immediately so refresh doesn't re-fire
      window.history.replaceState(null, "", "/client-portal");

      (async () => {
        try {
          const { data } = await axios.post(
            `${API}/auth/google-session`,
            { session_id },
          );
          if (data.on_file) {
            await loadPortal();
          } else {
            setDenial({ email: data.email, name: data.name });
            setLoading(false);
          }
        } catch (err) {
          toast.error("Sign-in failed. Please try again.");
          setLoading(false);
        }
      })();
    } else {
      loadPortal();
    }
  }, []);

  const loadPortal = async () => {
    setLoading(true);
    try {
      const me = await axios.get(`${API}/auth/client/me`);
      setProfile(me.data);
      const p = await axios.get(`${API}/client/portal`);
      setPortal(p.data);
      setPhoneEdit(p.data.profile?.phone || "");
      setBusinessEdit(p.data.profile?.business_name || "");
    } catch (err) {
      // Not signed in — show login state
      setProfile(null);
      setPortal(null);
    } finally {
      setLoading(false);
    }
  };

  const startGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/client-portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/client/logout`);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.debug("Client logout error:", err?.message);
    }
    setProfile(null);
    setPortal(null);
    toast.success("Signed out.");
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await axios.patch(`${API}/client/contact`, {
        phone: phoneEdit,
        business_name: businessEdit,
      });
      toast.success("Contact info saved.");
      await loadPortal();
    } catch (err) {
      toast.error("Could not save. Please try again.");
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <div
      className="relative min-h-screen bg-[#05050a] text-white overflow-hidden"
      data-testid="client-portal-page"
    >
      <FloatingOrbs colors={["#00f0ff", "#ff0055", "#a78bfa"]} />
      <AmbientDust count={20} color="#00f0ff" />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[10%] left-[50%] -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#6b21d9]/25 blur-[160px]" />
        <div className="absolute bottom-[-15%] right-[10%] w-[420px] h-[420px] rounded-full bg-[#00f0ff]/12 blur-[140px]" />
      </div>

      <div className="relative max-w-[960px] mx-auto px-5 md:px-10 py-14 md:py-20">
        {loading && (
          <div className="flex items-center justify-center py-24" data-testid="portal-loading">
            <Loader2 size={28} className="animate-spin text-[#00f0ff]" />
          </div>
        )}

        {!loading && !profile && (
          <PortalAuthSurface
            onGoogleClick={startGoogleSignIn}
            onSignedIn={loadPortal}
          />
        )}

        {!loading && profile && portal && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="portal-dashboard"
          >
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-10">
              <div className="flex items-center gap-4">
                {profile.picture ? (
                  <img
                    src={profile.picture}
                    alt={profile.name}
                    className="w-14 h-14 rounded-full border border-white/20"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
                    <UserIcon size={22} />
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
                    Welcome back
                  </div>
                  <div className="font-display font-black text-2xl md:text-3xl tracking-tight mt-1">
                    {profile.name || profile.email}
                  </div>
                  <div className="text-[11px] text-[#a395c3] mt-0.5">{profile.email}</div>
                </div>
              </div>
              <button
                onClick={logout}
                data-testid="portal-logout"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-white/80 hover:bg-white/[0.08] hover:text-white transition"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>

            {/* Grid — bookings + spotlight + invoices */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Bookings */}
              <Card icon={Mic} color="#00f0ff" title="My bookings" count={portal.bookings.length} testid="portal-bookings">
                {portal.bookings.length === 0 && <Empty text="No bookings yet." />}
                {portal.bookings.map((b) => (
                  <Row
                    key={b.id}
                    title={b.package_label || b.package}
                    sub={`${b.preferred_date}${b.preferred_time ? " · " + b.preferred_time : ""}`}
                    right={<StatusBadge status={b.status} />}
                    testid={`portal-booking-${b.id}`}
                  />
                ))}
              </Card>

              {/* Spotlight */}
              <Card icon={Send} color="#ff0055" title="Spotlight status" count={portal.spotlight_guest_slots.length} testid="portal-spotlight">
                {portal.spotlight_guest_slots.length === 0 && <Empty text="No Spotlight slots yet." />}
                {portal.spotlight_guest_slots.map((g) => (
                  <Row
                    key={g.id}
                    title={g.topic || g.business_name}
                    sub={`${g.preferred_date || "TBD"}${g.preferred_time ? " · " + g.preferred_time : ""}`}
                    right={<StatusBadge status={g.status} />}
                    testid={`portal-guest-${g.id}`}
                  />
                ))}
              </Card>

              {/* Invoices — live (Stripe-payable) first, then legacy receipts */}
              <Card
                icon={FileText}
                color="#a78bfa"
                title="Invoices & receipts"
                count={(portal.live_invoices?.length || 0) + (portal.invoices?.length || 0)}
                testid="portal-invoices"
              >
                {((portal.live_invoices?.length || 0) + (portal.invoices?.length || 0)) === 0 && (
                  <Empty text="No invoices on file." />
                )}
                {(portal.live_invoices || []).map((inv) => {
                  const unpaid = inv.status !== "paid";
                  return (
                    <Row
                      key={inv.id}
                      title={inv.number || `Invoice ${inv.id?.slice(0, 8)}`}
                      sub={inv.status === "paid" ? `Paid ${inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : ""}` : (inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "")}
                      right={
                        <div className="text-right flex items-center gap-3">
                          <div className="font-mono text-sm text-white">${(inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          {unpaid && (
                            <a
                              href={`/pay/${inv.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`portal-invoice-pay-${inv.id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#22c55e] text-black text-[11px] font-bold hover:bg-white transition-colors whitespace-nowrap"
                            >
                              Pay now →
                            </a>
                          )}
                          {!unpaid && (
                            <span className="text-[10px] px-2 py-1 rounded-full bg-[#22c55e]/15 text-[#22c55e] font-semibold uppercase tracking-widest">Paid</span>
                          )}
                        </div>
                      }
                      testid={`portal-live-invoice-${inv.id}`}
                    />
                  );
                })}
                {(portal.invoices || []).map((inv, i) => (
                  <Row
                    key={`legacy-${i}`}
                    title={inv.number || inv.description || `Invoice #${i + 1}`}
                    sub={inv.date || inv.paid_at || ""}
                    right={
                      <div className="text-right">
                        <div className="font-mono text-sm text-white">${inv.amount || 0}</div>
                        {inv.url && (
                          <a href={inv.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#00f0ff] hover:underline">
                            View receipt
                          </a>
                        )}
                      </div>
                    }
                    testid={`portal-invoice-${i}`}
                  />
                ))}
              </Card>

              {/* Chat history */}
              <Card icon={MessageSquare} color="#22c55e" title="Chat history" count={portal.chat_sessions.length} testid="portal-chats">
                {portal.chat_sessions.length === 0 && <Empty text="No chat history yet." />}
                {portal.chat_sessions.map((c) => (
                  <Row
                    key={c.session_id}
                    title={c.summary || "Chat session"}
                    sub={`${c.message_count} messages · ${new Date(c.started_at).toLocaleDateString()}`}
                    testid={`portal-chat-${c.session_id}`}
                  />
                ))}
              </Card>
            </div>

            {/* Deliverables — final videos/photos/audio + client-shared briefs */}
            <div
              className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 md:p-7"
              data-testid="portal-deliverables"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#a78bfa]/15 border border-[#a78bfa]/40 text-[#a78bfa] flex items-center justify-center">
                  <FolderOpen size={16} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#a78bfa]">
                    Your studio drive
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">
                    Deliverables & shared files
                  </div>
                  <div className="text-[#a395c3] text-xs mt-1">
                    Final cuts we deliver land here. You can also upload briefs
                    or reference media for your next session — we&apos;ll see
                    them on your card.
                  </div>
                </div>
              </div>
              <MediaUploader
                ownerType="client"
                ownerId={(profile?.email || "").toLowerCase()}
                authMode="client"
                title=""
                emptyLabel="Nothing shared yet. Upload a brief or wait for delivery."
                testIdPrefix="portal-media"
              />
            </div>

            {/* Update contact */}
            <div
              className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 md:p-7"
              data-testid="portal-contact-edit"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
                  <Phone size={16} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-[#00f0ff]">
                    Contact info
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">
                    Keep your details fresh
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  placeholder="Phone"
                  value={phoneEdit}
                  onChange={(e) => setPhoneEdit(e.target.value)}
                  data-testid="portal-phone-input"
                  className="rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
                <input
                  placeholder="Business name"
                  value={businessEdit}
                  onChange={(e) => setBusinessEdit(e.target.value)}
                  data-testid="portal-business-input"
                  className="rounded-xl bg-white/[0.04] border border-white/10 focus:border-[#00f0ff]/50 outline-none px-4 py-3 text-sm"
                />
              </div>
              <button
                onClick={saveContact}
                disabled={savingContact}
                data-testid="portal-save-contact"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition disabled:opacity-60"
              >
                {savingContact && <Loader2 size={14} className="animate-spin" />}
                Save changes
              </button>
            </div>
          </motion.div>
        )}
      </div>

      <DenialModal
        open={!!denial}
        email={denial?.email}
        name={denial?.name}
        onClose={() => {
          setDenial(null);
          navigate("/", { replace: true });
        }}
      />
    </div>
  );
};

const Card = ({ icon: Icon, color, title, count, children, testid }) => (
  <div
    className="rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 md:p-7"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center border"
          style={{ background: `${color}18`, borderColor: `${color}45`, color }}
        >
          <Icon size={16} />
        </div>
        <div className="font-display font-bold text-lg tracking-tight">{title}</div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
        {count} {count === 1 ? "item" : "items"}
      </span>
    </div>
    <div className="space-y-2">{children}</div>
  </div>
);

const Row = ({ title, sub, right, testid }) => (
  <div
    className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.02] border border-white/[0.06] px-4 py-3"
    data-testid={testid}
  >
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-white truncate">{title}</div>
      {sub && <div className="text-[11px] text-[#a395c3] mt-0.5 truncate">{sub}</div>}
    </div>
    {right}
  </div>
);

const Empty = ({ text }) => (
  <div className="text-[13px] text-[#a395c3]/70 italic px-1 py-2">{text}</div>
);

export default ClientPortal;
